// web-frontend/src/tutorial/TutorialOverlay.jsx
// Renders the spotlight, progress bar and tooltip for the current tutorial step.
// Reads state from TutorialContext. No theming logic of its own — uses CSS vars
// from the surrounding app + a light/dark switch via the documentElement
// data-theme attribute used by ThemeContext.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTutorial } from './TutorialContext';
import { tt } from './translations';
import useAppStore from '../shared/stores/appStore';
import useAuthStore from '../shared/stores/authStore';
import authService from '../shared/services/authService';
import i18nService from '../shared/services/i18n';
import logger from '../shared/utils/logger';
import './TutorialOverlay.css';

// 4 supported languages — kept here to avoid coupling to i18nService internals.
const PICKER_LANGUAGES = [
  { code: 'ko', flag: '🇰🇷', native: '한국어',  english: 'Korean'   },
  { code: 'en', flag: '🇺🇸', native: 'English',  english: 'English'  },
  { code: 'ja', flag: '🇯🇵', native: '日本語',   english: 'Japanese' },
  { code: 'zh', flag: '🇨🇳', native: '中文',     english: 'Chinese'  },
];

const TARGET_RESOLVE_RETRIES = 30;       // ~1.5s at 50ms each
const TARGET_RESOLVE_INTERVAL_MS = 50;
const FALLBACK_TIMEOUT_MS = 60000;       // hard fallback for misbehaving DOM observers

// Hook: track DOMRect of an element matched by selector.
function useTargetRect(selector, isActive) {
  const [rect, setRect] = useState(null);
  const [resolved, setResolved] = useState(false);
  // 🔥 [2026-06-30] 'failed' = 폴링을 모두 소진했는데도 타깃을 못 찾음(=영구 부재).
  //   resolved=false 만으로는 "아직 찾는 중" vs "포기"를 구분할 수 없어 stall 의 원인이 됨.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!isActive || !selector) {
      setRect(null);
      setResolved(false);
      setFailed(false);
      return undefined;
    }
    setFailed(false);

    let cancelled = false;
    let attempts = 0;
    let pollId = null;
    let observer = null;
    let element = null;
    let removalPollId = null; // 🔥 [2026-07-13] resolved 후 타깃 소멸 감시

    const update = () => {
      if (cancelled || !element) return;
      const r = element.getBoundingClientRect();
      // ignore zero-size rects (element collapsed / hidden)
      if (r.width === 0 && r.height === 0) return;
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        right: r.right,
        bottom: r.bottom,
      });
    };

    const tryResolve = () => {
      if (cancelled) return;
      element = document.querySelector(selector);
      if (element) {
        const r = element.getBoundingClientRect();
        // Wait until the element has non-zero layout AND is actually inside
        // the viewport. The mobile sidebar uses translateX(-100%) to hide
        // itself, which leaves the nav buttons with a normal width/height
        // but at negative x — we shouldn't draw a spotlight that's off the
        // screen, so we keep polling until the sidebar slides in.
        const hasSize = r.width > 0 && r.height > 0;
        const inViewport =
          r.right > 0 &&
          r.bottom > 0 &&
          r.left < window.innerWidth &&
          r.top < window.innerHeight;
        if (hasSize && inViewport) {
          setResolved(true);
          update();
          if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(update);
            observer.observe(element);
          }
          window.addEventListener('scroll', update, true);
          window.addEventListener('resize', update);
          // 🔥 [2026-07-13] 한 번 찾은 타깃이 DOM 에서 제거된 뒤 재등장하지 않으면(예: 계산
          //   오버레이가 사라졌는데 추천 0건이라 배지가 안 뜨는 케이스) failed 로 재전환해
          //   centered Next 폴백이 뜨게 함 — 기존엔 rect 가 stale 로 남아 90초 동결(W-33 잔여 stall).
          removalPollId = window.setInterval(() => {
            if (cancelled || !element) return;
            if (!document.contains(element)) {
              const again = document.querySelector(selector);
              if (again) {
                element = again;
                if (observer) {
                  observer.disconnect();
                  observer.observe(again);
                }
                update();
              } else {
                window.clearInterval(removalPollId);
                removalPollId = null;
                setResolved(false);
                setFailed(true);
              }
            }
          }, 800);
          return;
        }
      }
      attempts += 1;
      if (attempts >= TARGET_RESOLVE_RETRIES) {
        logger?.debug && logger.debug('[Tutorial] target not found', selector);
        setResolved(false);
        setFailed(true); // 🔥 영구 부재 확정 → 렌더에서 centered 폴백으로 전환
        return;
      }
      pollId = window.setTimeout(tryResolve, TARGET_RESOLVE_INTERVAL_MS);
    };

    tryResolve();

    return () => {
      cancelled = true;
      if (pollId) window.clearTimeout(pollId);
      if (removalPollId) window.clearInterval(removalPollId);
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [selector, isActive]);

  return { rect, resolved, failed };
}

// Compute tooltip position from target rect + preferred placement.
function computeTooltipPosition({ rect, placement, isMobile, viewport, tooltipSize }) {
  if (!rect || !viewport) return null;
  const margin = 12;
  const tw = tooltipSize.width || 320;
  const th = tooltipSize.height || 160;

  // Mobile bottom sheet handled in CSS.
  if (isMobile) {
    return { top: 'auto', left: 16, bottom: 16, right: 16, arrow: null };
  }

  let top = 0;
  let left = 0;
  let arrow = null;
  const targetCx = rect.left + rect.width / 2;
  const targetCy = rect.top + rect.height / 2;

  // Determine effective placement (flip if it overflows).
  let actual = placement || 'auto';
  if (actual === 'auto') {
    if (rect.bottom + margin + th < viewport.height) actual = 'bottom';
    else if (rect.top - margin - th > 0) actual = 'top';
    else if (rect.right + margin + tw < viewport.width) actual = 'right';
    else actual = 'left';
  } else {
    // Flip if not enough space.
    if (actual === 'right' && rect.right + margin + tw > viewport.width) actual = 'left';
    if (actual === 'left' && rect.left - margin - tw < 0) actual = 'right';
    if (actual === 'bottom' && rect.bottom + margin + th > viewport.height) actual = 'top';
    if (actual === 'top' && rect.top - margin - th < 0) actual = 'bottom';
  }

  if (actual === 'right') {
    left = rect.right + margin;
    top = Math.max(8, Math.min(targetCy - th / 2, viewport.height - th - 8));
    arrow = { side: 'left', top: Math.max(12, targetCy - top - 6), left: -6 };
  } else if (actual === 'left') {
    left = rect.left - margin - tw;
    top = Math.max(8, Math.min(targetCy - th / 2, viewport.height - th - 8));
    arrow = { side: 'right', top: Math.max(12, targetCy - top - 6), right: -6 };
  } else if (actual === 'bottom') {
    top = rect.bottom + margin;
    left = Math.max(8, Math.min(targetCx - tw / 2, viewport.width - tw - 8));
    arrow = { side: 'top', left: Math.max(12, targetCx - left - 6), top: -6 };
  } else {
    // top
    top = rect.top - margin - th;
    left = Math.max(8, Math.min(targetCx - tw / 2, viewport.width - tw - 8));
    arrow = { side: 'bottom', left: Math.max(12, targetCx - left - 6), bottom: -6 };
  }

  return { top, left, arrow, placement: actual };
}

function ProgressBar({ index, total, onSkip, lang, expanded, onToggleExpanded }) {
  const visualTotal = Math.max(1, total);
  const percent = Math.min(100, ((index + 1) / visualTotal) * 100);
  return (
    <div className="tutorial-progressbar">
      <span className="label">
        {tt('common.progress', lang, { i: index + 1, n: total })}
      </span>
      <div className="track">
        <div className="fill" style={{ width: percent + '%' }} />
      </div>
      <button
        type="button"
        className="toggle-btn"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        aria-label={expanded ? tt('common.hide_steps', lang) : tt('common.show_steps', lang)}
        title={expanded ? tt('common.hide_steps', lang) : tt('common.show_steps', lang)}
      >
        <span className={'chev ' + (expanded ? 'up' : 'down')} aria-hidden="true" />
      </button>
      <button type="button" className="skip-btn" onClick={onSkip}>
        {tt('common.skip', lang)}
      </button>
    </div>
  );
}

// Centered card that lets the user pick the app language as the very first
// tutorial step. Saves to DB (best-effort) + appStore (which syncs i18nService).
function LanguagePickerCard({ currentLang, onSelect, lang }) {
  const [isLoading, setIsLoading] = useState(false);
  const [pendingCode, setPendingCode] = useState(null);

  const handleClick = async (code) => {
    if (isLoading) return;
    setIsLoading(true);
    setPendingCode(code);
    try {
      await onSelect(code);
    } finally {
      // Component will unmount on advance; guard anyway.
      setIsLoading(false);
      setPendingCode(null);
    }
  };

  return (
    <div
      className="tutorial-tooltip centered tutorial-language-picker"
      role="dialog"
      aria-modal="true"
      aria-label={tt('picker.title', lang)}
    >
      <div className="picker-header">
        <span className="picker-globe" aria-hidden="true">🌐</span>
        <h3>{tt('picker.title', lang)}</h3>
        <p className="picker-subtitle">{tt('picker.subtitle', lang)}</p>
      </div>
      <div className="picker-options">
        {PICKER_LANGUAGES.map((L) => {
          const isSelected = currentLang === L.code;
          const isPending = pendingCode === L.code;
          return (
            <button
              key={L.code}
              type="button"
              className={'picker-option' + (isSelected ? ' selected' : '')}
              onClick={() => handleClick(L.code)}
              disabled={isLoading}
              aria-pressed={isSelected}
            >
              <span className="picker-flag" aria-hidden="true">{L.flag}</span>
              <span className="picker-text">
                <span className="picker-native">{L.native}</span>
                <span className="picker-english">{L.english}</span>
              </span>
              {isPending && <span className="picker-spinner" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      <p className="picker-note">{tt('picker.note', lang)}</p>
    </div>
  );
}

// Renders the full checklist of steps directly below the progress bar.
function StepsPanel({ steps, currentIndex, lang }) {
  return (
    <div
      className="tutorial-steps-panel"
      role="region"
      aria-label={tt('common.steps_panel_label', lang)}
    >
      <ol className="tutorial-steps-list">
        {steps.map((s, idx) => {
          const status = idx < currentIndex ? 'done' : idx === currentIndex ? 'current' : 'upcoming';
          return (
            <li
              key={s.id + '-' + idx}
              className={'tutorial-step-row status-' + status}
              aria-current={status === 'current' ? 'step' : undefined}
            >
              <span className={'status-icon ' + status} aria-hidden="true">
                {status === 'done' ? '✓' : status === 'current' ? '●' : '○'}
              </span>
              <span className="step-num">{idx + 1}.</span>
              <span className="step-title">{tt(s.titleKey, lang)}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function SpotlightCutout({ rect, padding = 8, blockClicks, clickable }) {
  if (!rect) return null;
  const t = Math.max(0, rect.top - padding);
  const l = Math.max(0, rect.left - padding);
  const r = Math.min(window.innerWidth, rect.right + padding);
  const b = Math.min(window.innerHeight, rect.bottom + padding);
  const w = window.innerWidth;
  const h = window.innerHeight;

  return (
    <>
      <div className="tutorial-mask" style={{ top: 0, left: 0, width: w, height: t }} />
      <div className="tutorial-mask" style={{ top: b, left: 0, width: w, height: Math.max(0, h - b) }} />
      <div className="tutorial-mask" style={{ top: t, left: 0, width: l, height: b - t }} />
      <div
        className="tutorial-mask"
        style={{ top: t, left: r, width: Math.max(0, w - r), height: b - t }}
      />
      <div
        className={'tutorial-spotlight-ring' + (clickable ? ' clickable' : '')}
        style={{ top: t, left: l, width: r - l, height: b - t }}
      />
      {blockClicks && (
        <div
          className="tutorial-click-blocker"
          style={{ top: t, left: l, width: r - l, height: b - t }}
        />
      )}
    </>
  );
}

function CenteredBackdrop({ onClick }) {
  return (
    <div
      className="tutorial-mask"
      style={{ top: 0, left: 0, right: 0, bottom: 0, width: '100vw', height: '100vh' }}
      onClick={onClick}
    />
  );
}

export default function TutorialOverlay() {
  const {
    isActive,
    steps,
    stepIndex,
    isMobile,
    isSidebarOpen,
    advance,
    goBack,
    finish,
    navigate,
  } = useTutorial();
  const location = useLocation();

  const lang = useAppStore((s) => s.language) || 'en';
  const setLanguageFn = useAppStore((s) => s.setLanguage);
  const authUser = useAuthStore((s) => s.user);
  const updateAuthUser = useAuthStore((s) => s.updateUser);

  const step = isActive && steps[stepIndex] ? steps[stepIndex] : null;

  // Language picker handler — persists to DB + appStore + i18nService and
  // advances the tutorial. Safe to call even when offline (DB call is best-
  // effort; local state always wins so the rest of the tour still uses the
  // chosen language). We force i18nService.changeLanguage explicitly because
  // appStore.setLanguage bails out when the new code matches its current
  // value, and i18nService can drift out of sync with the store on some
  // login paths — calling i18nService directly guarantees a re-sync.
  const handleLanguagePicked = useCallback(async (code) => {
    if (authUser) {
      try {
        await authService.updateProfile({ preferred_language: code });
        if (typeof updateAuthUser === 'function') {
          updateAuthUser({ ...authUser, preferred_language: code });
        }
      } catch (e) {
        logger?.warn && logger.warn('[Tutorial] preferred_language DB sync failed', e);
      }
    }
    try {
      await i18nService.changeLanguage(code);
    } catch (e) {
      logger?.warn && logger.warn('[Tutorial] i18nService.changeLanguage failed', e);
    }
    if (typeof setLanguageFn === 'function') {
      try {
        await setLanguageFn(code);
      } catch (e) {
        logger?.warn && logger.warn('[Tutorial] appStore.setLanguage failed', e);
      }
    }
    // Tiny delay so React commits the language change before the next step's
    // tooltip mounts and reads tt(...).
    window.setTimeout(() => advance(), 80);
  }, [authUser, updateAuthUser, setLanguageFn, advance]);

  // Step list panel toggle (collapsed by default).
  const [stepsPanelOpen, setStepsPanelOpen] = useState(false);
  const toggleStepsPanel = useCallback(() => setStepsPanelOpen((v) => !v), []);
  // Reset panel state whenever the tutorial restarts.
  useEffect(() => {
    if (!isActive) setStepsPanelOpen(false);
  }, [isActive]);

  // 🔥 [2026-07-13] 튜토리얼 마스크(z-index 9000)가 하단 네비를 덮어 홈 등 탭이 눌리지 않던 문제.
  //   네비는 .main-content(z-index:1) 스태킹 컨텍스트에 갇혀 z-index 로는 마스크 위로 올릴 수 없으므로,
  //   캡처 단계에서 탭 좌표 아래(마스크 뒤)에 하단 네비 항목이 있는지 elementsFromPoint 로 확인해
  //   있으면 투어를 종료(Skip 과 동일)하고 원래 탭한 네비 항목으로 클릭을 재전달한다.
  //   재시청은 설정 > 튜토리얼 다시 보기.
  useEffect(() => {
    if (!isActive) return undefined;
    document.body.classList.add('tutorial-active');
    const onNavClickCapture = (e) => {
      if (typeof document.elementsFromPoint !== 'function') return;
      const stack = document.elementsFromPoint(e.clientX, e.clientY);
      // 🔥 [2026-07-13 2차] 스택 전체에서 네비를 찾으면 사이드바 탭(예: 매칭 스텝의 'AI 매칭으로
      //   이동')도 그 아래 숨은 하단 네비에 걸려 투어가 종료되는 오작동 → 튜토리얼 레이어를 제외한
      //   '최상위' 요소가 하단 네비일 때(=사용자가 실제로 네비를 탭했을 때)만 탈출 처리.
      const firstReal = stack.find((el) => !(el.closest && el.closest('.tutorial-root')));
      const navItem = firstReal && firstReal.closest ? firstReal.closest('.mobile-bottom-nav') && firstReal : null;
      if (!navItem) return;
      e.preventDefault();
      e.stopPropagation();
      finish({ skipped: true });
      window.setTimeout(() => {
        const clickable = navItem.closest('a, button') || navItem;
        if (clickable && typeof clickable.click === 'function') clickable.click();
      }, 50);
    };
    document.addEventListener('click', onNavClickCapture, true);
    return () => {
      document.body.classList.remove('tutorial-active');
      document.removeEventListener('click', onNavClickCapture, true);
    };
  }, [isActive, finish]);

  // Viewport size tracking (for placement math)
  const [viewport, setViewport] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  useEffect(() => {
    const onR = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);

  // Tooltip ref to measure size
  const tooltipRef = useRef(null);
  const [tooltipSize, setTooltipSize] = useState({ width: 320, height: 160 });
  useEffect(() => {
    if (!tooltipRef.current) return;
    const el = tooltipRef.current;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setTooltipSize({ width: r.width, height: r.height });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => {
      if (ro) ro.disconnect();
    };
  }, [step && step.id]);

  // Resolve target rect (only for spotlight steps).
  const targetSelector = step && step.type === 'spotlight' ? step.target : null;
  const { rect: targetRect, resolved: targetResolved, failed: targetFailed } = useTargetRect(targetSelector, !!step);

  // Guard against double-advance from racing observers (e.g. class_added
  // observer firing in the same tick as a condition-driven skip).
  const lastAdvancedStepIdRef = useRef(null);
  const safeAdvance = useCallback(() => {
    if (!step) return;
    if (lastAdvancedStepIdRef.current === step.id) return;
    lastAdvancedStepIdRef.current = step.id;
    advance();
  }, [step, advance]);

  // Skip step if condition returns false.
  useEffect(() => {
    if (!step) return;
    if (typeof step.condition !== 'function') return;
    let pass = false;
    try {
      pass = !!step.condition({
        isMobile,
        isSidebarOpen,
        location,
      });
    } catch (e) {
      pass = true; // fail-open
    }
    if (!pass) {
      logger?.debug && logger.debug('[Tutorial] skip by condition', step.id);
      // Defer to next tick to avoid setState-in-render issues.
      const id = window.setTimeout(() => safeAdvance(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [step, isMobile, isSidebarOpen, location, safeAdvance]);

  // Advance triggers.
  useEffect(() => {
    if (!step) return undefined;
    if (typeof step.condition === 'function') {
      // If condition is false we're about to skip; don't bother wiring triggers.
      try {
        const pass = step.condition({ isMobile, isSidebarOpen, location });
        if (!pass) return undefined;
      } catch (_e) {
        // continue
      }
    }
    const adv = step.advance || { type: 'next_button' };

    let cleanup = () => {};
    let timeoutId = null;

    if (step.timeout) {
      timeoutId = window.setTimeout(() => {
        logger?.debug && logger.debug('[Tutorial] step timeout', step.id);
        safeAdvance();
      }, Math.max(2000, step.timeout));
    } else {
      // Generic safety fallback so a misbehaving step never traps the user.
      timeoutId = window.setTimeout(() => {
        // Only auto-advance non-clickable wait/spotlight steps; user-driven
        // steps (next_button, click_target) should remain user-controlled.
        if (adv.type === 'dom_added' || adv.type === 'class_added' || adv.type === 'route') {
          logger?.warn && logger.warn('[Tutorial] safety timeout for', step.id);
          safeAdvance();
        }
      }, FALLBACK_TIMEOUT_MS);
    }

    if (adv.type === 'route') {
      if (location.pathname === adv.path) {
        // Already on target route — advance immediately on enter.
        const id = window.setTimeout(() => safeAdvance(), 50);
        cleanup = () => window.clearTimeout(id);
      }
      // Else: location effect below will handle it via re-render.
    } else if (adv.type === 'dom_added') {
      const check = () => {
        if (document.querySelector(adv.selector)) {
          safeAdvance();
          return true;
        }
        return false;
      };
      if (!check()) {
        const observer = new MutationObserver(() => {
          if (check()) observer.disconnect();
        });
        observer.observe(document.body, { childList: true, subtree: true });
        cleanup = () => observer.disconnect();
      }
    } else if (adv.type === 'class_added') {
      const check = () => {
        const el = document.querySelector(adv.target);
        if (el && el.classList.contains(adv.className)) {
          safeAdvance();
          return true;
        }
        return false;
      };
      if (!check()) {
        // Watch the body for the target's appearance and class changes.
        const observer = new MutationObserver(() => {
          if (check()) observer.disconnect();
        });
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
          childList: true,
        });
        cleanup = () => observer.disconnect();
      }
    } else if (adv.type === 'click_target' && step.target) {
      let detached = false;
      const onClick = () => {
        if (detached) return;
        detached = true;
        // Slight delay so the underlying handler runs first.
        window.setTimeout(() => safeAdvance(), 80);
      };
      const tryAttach = () => {
        const el = document.querySelector(step.target);
        if (!el) {
          const id = window.setTimeout(tryAttach, 100);
          cleanup = () => window.clearTimeout(id);
          return;
        }
        el.addEventListener('click', onClick, { once: true, capture: true });
        cleanup = () => {
          if (!detached) el.removeEventListener('click', onClick, { capture: true });
        };
      };
      tryAttach();
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step && step.id, location.pathname]);

  // Watch route changes for route advance type (separate effect for reliability).
  useEffect(() => {
    if (!step) return;
    const adv = step.advance;
    if (!adv || adv.type !== 'route') return;
    if (location.pathname === adv.path) {
      const id = window.setTimeout(() => safeAdvance(), 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [step, location.pathname, safeAdvance]);

  // Belt-and-suspenders for class_added: some mobile WebViews and browsers
  // don't bubble class mutations through `document.body` subtree observers in
  // time. We additionally key off TutorialContext's own per-element observer
  // (exposed as isSidebarOpen) AND a low-rate poll, so the open-menu step
  // reliably advances once the sidebar slides in.
  useEffect(() => {
    if (!step) return undefined;
    const adv = step.advance;
    if (!adv || adv.type !== 'class_added') return undefined;
    const checkAndMaybeAdvance = () => {
      try {
        const el = document.querySelector(adv.target);
        if (el && el.classList.contains(adv.className)) {
          safeAdvance();
          return true;
        }
      } catch (_e) { /* ignore */ }
      return false;
    };

    // 1) immediate re-check on isSidebarOpen flip
    if (
      adv.target === '.sidebar' &&
      adv.className === 'mobile-open' &&
      isSidebarOpen
    ) {
      const tid = window.setTimeout(() => safeAdvance(), 50);
      return () => window.clearTimeout(tid);
    }
    // 2) low-rate poll (300ms) as final fallback for any class_added step
    const intervalId = window.setInterval(() => {
      if (checkAndMaybeAdvance()) {
        window.clearInterval(intervalId);
      }
    }, 300);
    return () => window.clearInterval(intervalId);
  }, [step, isSidebarOpen, safeAdvance]);

  // ---- Render -----------------------------------------------------------
  if (!step) return null;

  const isLanguagePicker = step.type === 'language_picker';
  // Picker shares the centered backdrop treatment.
  const isCentered = step.type === 'centered' || isLanguagePicker;
  const advType = (step.advance && step.advance.type) || 'next_button';
  // 🔥 [2026-06-30] 스포트라이트 타깃이 끝내 안 나타나면(영구 부재) centered 카드 + Next 로 폴백.
  //   (예: 매칭 AI 버튼/탄소 위젯이 특정 테넌트·역할에서 렌더되지 않을 때 무한 대기→stall 방지)
  const targetMissing = step.type === 'spotlight' && !isLanguagePicker && targetFailed;
  const renderCentered = isCentered || targetMissing;
  // 타깃 부재 폴백이면 advance 타입과 무관하게 Next 버튼을 노출 → 사용자가 항상 진행 가능.
  const showNextButton = advType === 'next_button' || targetMissing;
  // Block clicks on the spotlighted target only when advance is next_button
  // (user shouldn't navigate or trigger the action; we want them to read).
  const blockTargetClicks = !renderCentered && advType === 'next_button';

  const titleText = tt(step.titleKey, lang);
  const bodyText = tt(step.bodyKey, lang);

  // Position
  let style = {};
  let arrow = null;
  let canRenderTooltip = true;
  if (!renderCentered) {
    const pos = computeTooltipPosition({
      rect: targetRect,
      placement: step.placement,
      isMobile,
      viewport,
      tooltipSize,
    });
    if (!pos) {
      // Target not yet resolved — keep tooltip off-screen so it doesn't flash.
      canRenderTooltip = targetResolved;
      if (!isMobile) {
        style = { top: -9999, left: -9999, opacity: 0 };
      } else {
        style = {};
      }
    } else if (pos.bottom != null) {
      // Mobile bottom sheet
      style = {};
    } else {
      style = { top: pos.top, left: pos.left };
      arrow = pos.arrow;
    }
  }

  const isLastStep = stepIndex >= steps.length - 1;

  const handleSkip = () => finish({ skipped: true });
  const handleNext = () => {
    // Last step's Next button completes the tutorial. Without this branch
    // advance() bails (next >= steps.length) and leaves the user stuck.
    if (isLastStep) {
      finish({ skipped: false });
    } else {
      advance();
    }
  };
  const handlePrev = () => goBack();

  const onCtaClick = () => {
    if (!step.cta) return;
    const action = step.cta.action || {};
    if (action.type === 'navigate' && action.path) {
      navigate(action.path);
    }
    if (step.cta.finishAfter) {
      // Mark done — user has taken the recommended action.
      finish({ skipped: false });
    } else if (step.cta.advanceAfter) {
      if (isLastStep) {
        finish({ skipped: false });
      } else {
        advance();
      }
    }
  };

  const tooltipNode = (
    <div
      ref={tooltipRef}
      className={'tutorial-tooltip' + (renderCentered ? ' centered' : '')}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label={titleText}
    >
      {arrow && !isMobile && (
        <span
          className="arrow"
          style={(() => {
            const a = {};
            if (arrow.side === 'top') {
              a.top = arrow.top;
              a.left = arrow.left;
            } else if (arrow.side === 'bottom') {
              a.bottom = arrow.bottom;
              a.left = arrow.left;
            } else if (arrow.side === 'left') {
              a.left = arrow.left;
              a.top = arrow.top;
            } else if (arrow.side === 'right') {
              a.right = arrow.right;
              a.top = arrow.top;
            }
            return a;
          })()}
        />
      )}
      <h3>{titleText}</h3>
      <p>{bodyText}</p>
      <div className="actions">
        <div className="actions-left">
          {stepIndex > 0 && (
            <button type="button" className="btn btn-link" onClick={handlePrev}>
              {tt('common.prev', lang)}
            </button>
          )}
          {step.cta && step.cta.labelKey && (
            <button type="button" className="btn btn-link" onClick={onCtaClick}>
              {tt(step.cta.labelKey, lang)}
            </button>
          )}
        </div>
        <div className="actions-right">
          {showNextButton && (
            <button type="button" className="btn btn-primary" onClick={handleNext}>
              {stepIndex === steps.length - 1
                ? tt('common.done', lang)
                : tt('common.next', lang)}
            </button>
          )}
          {!showNextButton && advType === 'click_target' && (
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              {/* visual hint only; no advance button */}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const root = (
    <div className="tutorial-root">
      <div className="tutorial-top-stack">
        <ProgressBar
          index={stepIndex}
          total={steps.length}
          onSkip={handleSkip}
          lang={lang}
          expanded={stepsPanelOpen}
          onToggleExpanded={toggleStepsPanel}
        />
        {stepsPanelOpen && (
          <StepsPanel
            steps={steps}
            currentIndex={stepIndex}
            lang={lang}
          />
        )}
      </div>
      {renderCentered ? (
        <CenteredBackdrop />
      ) : targetRect ? (
        <SpotlightCutout
          rect={targetRect}
          padding={step.spotlightPadding || 8}
          blockClicks={blockTargetClicks}
          clickable={!blockTargetClicks}
        />
      ) : (
        // Target not (yet) resolved — show full-screen mask so the page
        // is dimmed while we resolve.
        <CenteredBackdrop />
      )}
      {isLanguagePicker ? (
        <LanguagePickerCard
          currentLang={lang}
          onSelect={handleLanguagePicked}
          lang={lang}
        />
      ) : (
        canRenderTooltip && tooltipNode
      )}
    </div>
  );

  if (typeof document === 'undefined') return root;
  return createPortal(root, document.body);
}
